import { zodResolver } from '@hookform/resolvers/zod';
import { Grid, Typography, TextField, TextFieldProps } from '@mui/material';
import { ConfirmationDialogContentHandle } from '@procureai/ui-mui';
import { PropsWithChildren, forwardRef, useImperativeHandle } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { useFormFieldErrorTranslation } from '~/hooks';

import { messageFormDataDefault, messageFormDataSchema, MessageFormData } from './MessageForm.schema';

const commonInputProps: Pick<TextFieldProps<'outlined'>, 'margin' | 'fullWidth' | 'variant' | 'size'> = {
  margin: 'dense',
  fullWidth: true,
  variant: 'outlined',
  size: 'small',
};

export interface MessageFormProps {
  onConfirm?: (data: MessageFormData) => void;
  i18nKeyPrefix: string;
}

export const MessageForm = forwardRef<ConfirmationDialogContentHandle, PropsWithChildren<MessageFormProps>>(
  function CommentDialogForm({ onConfirm, i18nKeyPrefix }, ref) {
    const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: i18nKeyPrefix });
    const { errorToHelperText } = useFormFieldErrorTranslation();

    const form = useForm<MessageFormData>({
      mode: 'onTouched',
      resolver: zodResolver(messageFormDataSchema),
      defaultValues: messageFormDataDefault,
    });

    const handleSubmit = form.handleSubmit((data) => {
      if (onConfirm) {
        onConfirm(data);
      }
    });

    useImperativeHandle(ref, () => ({
      confirm: handleSubmit,
    }));

    return (
      <FormProvider {...form}>
        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
          <Grid
            container
            direction="row"
            spacing={1}
            sx={{
              justifyContent: 'flex-start',
              alignContent: 'flex-start',
            }}
          >
            <Grid size={12}>
              <Typography
                sx={{
                  marginBottom: 4,
                }}
              >
                <Trans t={t} i18nKey="description" />
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 8,
              }}
            >
              <Controller
                control={form.control}
                name="message"
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label={t('message')}
                    error={!!error}
                    helperText={errorToHelperText(error)}
                    multiline
                    minRows={4}
                    maxRows={12}
                    {...commonInputProps}
                    {...field}
                    value={field.value || ''}
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    );
  },
);
